import React, { useCallback, useEffect, useState, useRef } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons';
import logo from '../../assets/logo.png';
import "../../styles/MyProfile.css";
import { faBriefcaseClock, faVolumeHigh, faDownload } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

const MyDigitalCardTalking = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState.user._id;

  const [profileData, setProfileData] = useState({
    userId: authState.user._id,
    name: authState.user.name,
    gender: 0,
    dob: '',
    email: authState.user.email,
    mobile: '',
    city: '',
    institution: '',
    socialLinks: '',
    personalPageLink: '',
    think5whysCode: authState.user.think5whyscode,
    image: '',
  });

  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [ttsLang, setTtsLang] = useState("en-IN");  

  const cardRef = useRef(null); // Ref for capturing card for download

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const fetchUserProfile = useCallback(() => {
    const config = {
      url: "/myprofile/byid",
      method: 'post',
      data: { userId: userId },
      headers: { Authorization: authState.token },
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setProfileExists(true);
          const { profile } = response;
          setProfileId(profile._id);
          setProfileData({
            userId: profile.userId,
            name: profile.name,
            gender: profile.gender,
            dob: profile.dob,
            email: profile.email,
            mobile: profile.mobile,
            city: profile.city,
            institution: profile.institution,
            socialLinks: profile.socialLinks,
            personalPageLink: profile.personalPageLink,
            think5whysCode: profile.think5whysCode,
            image: profile.image
          });
        }
      })
      .catch(() => {});
  }, [userId, fetchData, authState.token]);

  // --------------------- TTS FUNCTION ---------------------
  const speakCard = () => {
    const msg = new SpeechSynthesisUtterance();
    msg.lang = ttsLang;
    msg.text =
      `Here is the digital card of ${profileData.name}. 
       Mobile number ${profileData.mobile}. 
       Email ${profileData.email}. 
       Institution ${profileData.institution}. 
       City ${profileData.city}. 
       Think 5 Whys Code ${profileData.think5whysCode}.`;
    speechSynthesis.cancel();
    speechSynthesis.speak(msg);
  };

  // --------------------- DOWNLOAD FUNCTION ---------------------
  const downloadCard = () => {
    if (!cardRef.current) return;

    // Hide buttons before capture
    const buttons = cardRef.current.querySelectorAll('.hide-for-download');
    buttons.forEach(btn => btn.style.display = 'none');

    html2canvas(cardRef.current, { scale: 2 }).then(canvas => {
      const link = document.createElement('a');
      link.download = `${profileData.name}_DigitalCard.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      // Restore buttons
      buttons.forEach(btn => btn.style.display = '');
    });
  };

  return (
    <div className="flex flex-col justify-center items-center p-4 relative">

      {/* CARD UI with QR included */}
      <div
        ref={cardRef}
        className="bg-gradient-to-r from-gray-200 to-gray-400 border border-gray-600 rounded-lg shadow-lg w-full max-w-sm p-4 flex flex-col items-center"
      >
        <img
          src={profileData.image || logo}
          alt="Profile"
          className="w-32 h-32 rounded-full border-2 border-gray-300 p-1 mb-4"
        />

        <h2 className="text-gray-800 font-bold text-2xl mb-2 text-center">{profileData.name}</h2>
        <p className="text-sm text-gray-600 text-center">ID#: {profileData.think5whysCode}</p>
        <p className="text-sm text-gray-600 text-center">Institution: {profileData.institution}</p>
        <p className="text-sm text-gray-600 text-center">Email: {profileData.email}</p>
        <p className="text-sm text-gray-600 text-center">Mobile: {profileData.mobile}</p>
        <p className="text-sm text-gray-600 text-center mb-4">Place: {profileData.city}</p>

        {/* SOCIAL ICONS */}
        <div className="flex justify-around mb-4">
          <a href={profileData.socialLinks?.facebook} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-600" icon={faFacebook} size="lg" />
          </a>
          <a href={profileData.socialLinks?.instagram} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-pink-600" icon={faInstagram} size="lg" />
          </a>
          <a href={profileData.socialLinks?.twitter} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-400" icon={faTwitter} size="lg" />
          </a>
          <a href={profileData.personalPageLink} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-700" icon={faLinkedin} size="lg" />
          </a>
          <a href={profileData.personalPageLink} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-gray-600" icon={faBriefcaseClock} size="lg" />
          </a>
        </div>

        {/* QR Code */}
        <div className="bg-white p-2 rounded-md shadow-md mb-4">
          <QRCode value={`https://www.think5whys.com/pub/${profileData.think5whysCode}`} size={128} />
        </div>

        <p className="bg-gray-100 text-gray-700 text-center text-sm p-2 rounded-md mb-2">
          Powered by
          <img src={logo} alt="Think5Whys" className="inline-block w-6 h-6 mx-2" />
          think5whys.com
        </p>
      </div>
      <div className="mt-4 flex flex-col items-center gap-3 hide-for-download border-t border-blue-400 lg:w-2/4">
          <select
            className="bg-white text-gray-700 px-3 py-2 h-10 rounded border w-40 mt-2"
            value={ttsLang}
            onChange={(e) => setTtsLang(e.target.value)}
          >
            <option value="en-IN">English</option>
            <option value="hi-IN">Hindi</option>
            <option value="mr-IN">Marathi</option>
          </select>

          <div className='grid grid-cols-2 gap-4 place-items-center w-full'>
            <button
              onClick={speakCard}
              className="bg-blue-600 text-white w-full px-4 py-2 rounded shadow flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faVolumeHigh} />
              Listen
            </button>

            <button
              onClick={downloadCard}
              className="bg-green-600 text-white px-4 py-2 rounded shadow flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download
            </button>
          </div>
        </div>
    </div>
  );
};

export default MyDigitalCardTalking;
